import axios, { CancelToken } from "axios";

import config from "@Config/index";
import { logger } from "helpers/logger";
import urlJoin from "helpers/urljoin";
import { GeoData, getPersonalizedSearchSuggestions } from "services/searchSuggestionsService";
import { getGeoDataForSearchSuggestions } from "@Utils/getGeoData";
import { getDistanceData } from "@Pages/Search/components/SearchListItem/helper";

import type { Market } from "../../market";
import type { StayRow, University } from "../../data/homeV3Reducer";

const MARKET_COUNTRY: Partial<Record<Market, string>> = {
  GB: "United Kingdom",
  US: "United States",
  AU: "Australia",
};

export type { University };

/**
 * The top universities the site already suggests in search, personalised to the
 * visitor's country — the same `getPersonalizedSearchSuggestions` call (and geo lookup)
 * the search modals make. The UK list is used, as the rail's demo market; campus and
 * pathway duplicates ("… (Clifton Campus)", "INTO …") are dropped and each name kept
 * once, so the pills read as distinct universities.
 */
/**
 * `market` (an ISO code, see ../../market.ts) picks whose universities to show: the UK's,
 * the United States' or Australia's. Each page asks as a visitor from that country would, so the suggestions lead with its universities.
 */
export const fetchTopUniversities = async (market: Market = "GB"): Promise<University[]> => {
  try {
    // The geo lookup reads the browser's storage; a known market (and the server
    // render) doesn't need it.
    const geo = MARKET_COUNTRY[market]
      ? ({ sourceCountry: null, sourceCity: null } as unknown as GeoData)
      : await getGeoDataForSearchSuggestions();
    const res = await getPersonalizedSearchSuggestions({
      sourceCountry: MARKET_COUNTRY[market] || geo.sourceCountry || "United Kingdom",
      sourceCity: MARKET_COUNTRY[market] ? undefined : geo.sourceCity,
    } as GeoData);
    if (res === "error") return [];
    const countries: any[] = res?.data?.data?.data || [];
    const pick =
      countries.find((c) => c.country_short_name === market) ||
      countries.find((c) => (c.top_universities?.data || []).length >= 4) ||
      countries[0];
    const seen = new Set<string>();
    return (pick?.top_universities?.data || [])
      .filter((u: any) => u?.canonical_name && !/campus|^into\b/i.test(u.name))
      .filter((u: any) => !seen.has(u.name) && seen.add(u.name))
      .slice(0, 8)
      .map((u: any) => ({ name: u.name, canonicalName: u.canonical_name }));
  } catch (err) {
    logger.log(`HomeV2 fetchTopUniversities: ${err}`);
    return [];
  }
};

/**
 * Properties near one university, each carrying its distance to it. The same
 * `GET /api/v1/inventories` call the homepage rails make, plus what the Search page adds
 * for a university: `destination_coordinates` (read from `GET /api/v0/regions/:canonical`)
 * so `destination_distances` is measured to the campus.
 */
export const fetchStaysNear = async (
  canonicalName: string,
  { lang, currency }: { lang: string; currency: string },
  token: CancelToken,
): Promise<any[]> => {
  try {
    const region = await axios.get(urlJoin(config.API_URL, `api/v0/regions/${canonicalName}`), {
      cancelToken: token,
    });
    const found = region?.data?.data;
    const coords = found?.location?.location_coordinates || found?.location_coordinates;
    const res = await axios.get(urlJoin(config.API_URL, "api/v1/inventories"), {
      cancelToken: token,
      headers: { "X-Use-Java-Api": "true" },
      params: {
        p: 1,
        limit: 12,
        only: "id,name,parent_id,pricing,images,videos,location,canonical_name,tags,features,source_link",
        sort_key: "relevance_sorting_v2",
        region_radius: 20000,
        region_type: "circle",
        use_open_search: true,
        sort_order: "desc",
        statuses: "active",
        available: true,
        region_canonical_name: canonicalName,
        ...(coords?.lat ? { destination_coordinates: `${coords.lat},${coords.lng}` } : {}),
        methods:
          "destination_distances,top_meta_distances,active_children_count,location_coordinates,reviews_count,reviews_rating,meta_short,feature_tags,amber_sales",
        lang,
        ...(currency ? { user_currency: currency } : {}),
        use_java_api: true,
      },
    });
    return res?.data?.data?.result || [];
  } catch (err) {
    if (!axios.isCancel(err)) logger.log(`HomeV2 fetchStaysNear: ${err}`);
    return [];
  }
};

/** The rail's rows, with their walking distance, closest first. */
export const toStayRows = (result: any[]): StayRow[] =>
  result
    .map((item) => {
      const d = item?.destination_distances && getDistanceData(item.destination_distances);
      const text = d?.WALKING?.distance;
      return { item, text: text && text !== "0 ft" ? text : "" };
    })
    // Closest first: the section is about being near campus.
    .sort((a, b) => parseFloat(a.text || "999") - parseFloat(b.text || "999"));
